import { useState, useEffect, useMemo } from 'react';
import { auditListEvents, auditSystemStatus, auditStorage, auditSecurityStats } from '../services/api';
import {
  ShieldCheck, ShieldWarning, HardDrives, Database, Robot, Clock,
  SignIn, SignOut, Key, UserPlus, Buildings, Receipt, Megaphone, Cpu,
  CheckCircle, Warning, Lock, ArrowsClockwise
} from '@phosphor-icons/react';

const EVENT_META = {
  login_success: { icon: SignIn, label: 'Login erfolgreich', tone: 'emerald' },
  login_failed: { icon: ShieldWarning, label: 'Login fehlgeschlagen', tone: 'red' },
  logout: { icon: SignOut, label: 'Logout', tone: 'slate' },
  password_changed: { icon: Key, label: 'Passwort geändert', tone: 'blue' },
  password_reset_requested: { icon: Key, label: 'Passwort-Reset angefragt', tone: 'amber' },
  password_reset_completed: { icon: Key, label: 'Passwort-Reset abgeschlossen', tone: 'emerald' },
  user_created: { icon: UserPlus, label: 'User angelegt', tone: 'violet' },
  user_deleted: { icon: UserPlus, label: 'User gelöscht', tone: 'red' },
  mandant_created: { icon: Buildings, label: 'Mandant angelegt', tone: 'violet' },
  mandant_deleted: { icon: Buildings, label: 'Mandant gelöscht', tone: 'red' },
  receipt_uploaded: { icon: Receipt, label: 'Beleg hochgeladen', tone: 'slate' },
  receipt_deleted: { icon: Receipt, label: 'Beleg gelöscht', tone: 'amber' },
  ai_check_performed: { icon: Robot, label: 'KI-Prüfung', tone: 'rose' },
  system_notice_updated: { icon: Megaphone, label: 'Wartungs-Hinweis geändert', tone: 'violet' },
  deploy_downloaded: { icon: HardDrives, label: 'Deploy-Script geladen', tone: 'amber' },
  wingman_action: { icon: Cpu, label: 'Wingman-Aktion', tone: 'cyan' },
};

const TONE_BG = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  slate: 'bg-slate-50 text-slate-600 border-slate-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
};

function bytesHuman(n) {
  if (!n || n <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${u[i]}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function SuperAdminSicherheitsAudit() {
  const [status, setStatus] = useState(null);
  const [storage, setStorage] = useState(null);
  const [secStats, setSecStats] = useState(null);
  const [events, setEvents] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [onlyWingman, setOnlyWingman] = useState(false);
  const [eventType, setEventType] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const params = { limit, skip };
      if (eventType) params.event_type = eventType;
      if (onlyWingman) params.only_wingman = true;
      const [s, st, sc, ev] = await Promise.all([
        auditSystemStatus(),
        auditStorage(),
        auditSecurityStats(),
        auditListEvents(params),
      ]);
      setStatus(s.data);
      setStorage(st.data);
      setSecStats(sc.data);
      setEvents(ev.data);
    } catch (e) {
      console.error('audit load failed', e);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [skip, eventType, onlyWingman]);

  const maxDailyFailed = useMemo(() => {
    if (!secStats?.daily_7d) return 0;
    return Math.max(1, ...secStats.daily_7d.map(d => d.failed || 0));
  }, [secStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-audit-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ShieldCheck weight="fill" className="w-8 h-8 text-violet-700" />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
              Sicherheits-Audit
            </h1>
            <p className="text-slate-500 mt-1 text-sm">System-Status, Speicherplatz, Aktivitäts-Log & Sicherheits-Statistik</p>
          </div>
        </div>
        <button
          onClick={() => loadAll()}
          disabled={refreshing}
          className="inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-2 text-sm hover:bg-slate-800 disabled:opacity-50 rounded"
          data-testid="audit-refresh-button"
        >
          <ArrowsClockwise weight="bold" className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* System Status Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="status-tiles">
        <StatusTile
          icon={Lock}
          label="Firewall"
          value={status?.firewall?.label}
          ok={status?.firewall?.active}
          hint={status?.firewall?.hint}
          testId="tile-firewall"
        />
        <StatusTile
          icon={HardDrives}
          label="Backups"
          value={status?.backups?.label}
          ok={status?.backups?.ok}
          hint={status?.backups?.last_backup ? `Letztes Backup: ${formatDate(status.backups.last_backup)}` : status?.backups?.hint}
          testId="tile-backups"
        />
        <StatusTile
          icon={ShieldCheck}
          label="SSL / HTTPS"
          value={status?.ssl?.label}
          ok={status?.ssl?.valid}
          hint={status?.ssl?.public_url}
          testId="tile-ssl"
        />
      </div>

      {/* Storage */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="storage-card">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <Database weight="bold" className="w-5 h-5 text-slate-700" />
          <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900">Speicherplatz – Beleg-Datenbank</h2>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border border-slate-100 p-4 rounded">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Belege gesamt</p>
            <p className="font-mono text-3xl font-black text-slate-900 mt-2">{storage?.receipts?.count ?? 0}</p>
          </div>
          <div className="border border-slate-100 p-4 rounded">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Datengröße</p>
            <p className="font-mono text-3xl font-black text-slate-900 mt-2">{storage?.receipts?.data_mb ?? 0} <span className="text-base text-slate-500">MB</span></p>
            <p className="text-xs text-slate-400 mt-1">{storage?.receipts?.data_gb ?? 0} GB</p>
          </div>
          <div className="border border-slate-100 p-4 rounded">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Disk frei (Host)</p>
            <p className="font-mono text-3xl font-black text-slate-900 mt-2">{bytesHuman(status?.host?.disk_free_bytes)}</p>
            <p className="text-xs text-slate-400 mt-1">von {bytesHuman(status?.host?.disk_total_bytes)}</p>
          </div>
        </div>
        {storage?.per_mandant && storage.per_mandant.length > 0 && (
          <div className="px-5 pb-5">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Top Mandanten nach Speicherbedarf</p>
            <div className="space-y-1">
              {storage.per_mandant.slice(0, 5).map(m => (
                <div key={m.mandant_id || 'none'} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm">
                  <span className="text-slate-700 truncate">{m.mandant_name || <em className="text-slate-400">unbekannt</em>}</span>
                  <span className="font-mono text-slate-500">{m.receipt_count} Belege · ~{bytesHuman(m.approx_bytes)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Security Stats */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="security-stats-card">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <ShieldWarning weight="bold" className="w-5 h-5 text-red-700" />
          <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900">Sicherheits-Statistik</h2>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="Fehl-Logins 24h" value={secStats?.failed_logins_24h} tone="red" />
            <MiniStat label="Fehl-Logins 7 T" value={secStats?.failed_logins_7d} tone="amber" />
            <MiniStat label="Erfolgreich 24h" value={secStats?.success_logins_24h} tone="emerald" />
            <MiniStat label="Aktive Sperren" value={secStats?.active_lockouts} tone="violet" />
          </div>

          {/* Daily bar chart */}
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Letzte 7 Tage – fehlgeschlagene Logins</p>
            <div className="flex items-end gap-2 h-32" data-testid="security-daily-chart">
              {secStats?.daily_7d?.map(d => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end">
                  <div
                    className="w-full bg-red-400 hover:bg-red-500 transition-colors rounded-t"
                    style={{ height: `${Math.max(2, (d.failed / maxDailyFailed) * 100)}%` }}
                    title={`${d.date}: ${d.failed} fehlgeschlagen, ${d.success} erfolgreich`}
                  />
                  <p className="text-[10px] text-slate-400 mt-1 truncate">{d.date.slice(5)}</p>
                  <p className="text-xs font-mono text-slate-700">{d.failed}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Top Offender IPs + Fail2Ban */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Top Quell-IPs (Fehl-Logins 7T)</p>
              {(secStats?.top_offender_ips_7d || []).length === 0 ? (
                <p className="text-sm text-slate-400 italic">Keine Fehl-Logins in den letzten 7 Tagen.</p>
              ) : (
                <div className="space-y-1">
                  {secStats.top_offender_ips_7d.map(o => (
                    <div key={o.ip} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm">
                      <span className="font-mono text-slate-700">{o.ip}</span>
                      <span className="font-mono text-red-700">{o.count}× · {formatDate(o.last_attempt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Fail2Ban-Integration</p>
              <div className="bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600 rounded">
                <p className="flex items-center gap-2 mb-1">
                  {secStats?.fail2ban?.configured
                    ? <CheckCircle weight="fill" className="w-4 h-4 text-emerald-600" />
                    : <Warning weight="fill" className="w-4 h-4 text-amber-500" />}
                  <span className="font-medium">
                    {secStats?.fail2ban?.configured ? 'Konfiguriert' : 'Platzhalter – nicht konfiguriert'}
                  </span>
                </p>
                <p className="text-xs">{secStats?.fail2ban?.note}</p>
                <p className="text-xs mt-2">Blockiert gesamt: <span className="font-mono">{secStats?.fail2ban?.blocked_total ?? 0}</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Log */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="activity-log-card">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Clock weight="bold" className="w-5 h-5 text-slate-700" />
            <h2 className="font-heading text-base sm:text-lg font-bold text-slate-900">Aktivitäts-Log</h2>
            <span className="text-xs text-slate-400">({events.total} Einträge gesamt)</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={eventType}
              onChange={(e) => { setSkip(0); setEventType(e.target.value); }}
              className="border border-slate-200 px-2 py-1 text-xs rounded"
              data-testid="event-type-filter"
            >
              <option value="">Alle Ereignisse</option>
              {Object.entries(EVENT_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={onlyWingman}
                onChange={(e) => { setSkip(0); setOnlyWingman(e.target.checked); }}
                data-testid="only-wingman-toggle"
              />
              <span>Nur Wingman</span>
            </label>
          </div>
        </div>

        <div className="divide-y divide-slate-100" data-testid="audit-events-list">
          {events.items.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">Keine Ereignisse gefunden.</div>
          ) : (
            events.items.map((ev, i) => {
              const meta = EVENT_META[ev.event_type] || { icon: Cpu, label: ev.event_type, tone: 'slate' };
              const Icon = meta.icon;
              return (
                <div key={i} className="px-5 py-3 flex items-start gap-3 hover:bg-slate-50">
                  <div className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded border ${TONE_BG[meta.tone]}`}>
                    <Icon weight="bold" className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 text-sm">{meta.label}</span>
                      {ev.by_wingman && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded" data-testid="wingman-badge">
                          <Cpu weight="bold" className="w-3 h-3" /> Wingman
                        </span>
                      )}
                      {ev.severity === 'warning' && <span className="text-[10px] uppercase font-bold text-amber-700">Warnung</span>}
                      {ev.severity === 'critical' && <span className="text-[10px] uppercase font-bold text-red-700">Kritisch</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      {ev.actor_email && <span className="font-mono">{ev.actor_email}</span>}
                      {ev.actor_role && <span className="ml-1 opacity-70">({ev.actor_role})</span>}
                      {ev.mandant_name && <span className="ml-2">· Mandant: <strong>{ev.mandant_name}</strong></span>}
                      {ev.ip && <span className="ml-2">· IP: <span className="font-mono">{ev.ip}</span></span>}
                      {ev.target && <span className="ml-2">· Ziel: <span className="font-mono truncate">{ev.target}</span></span>}
                    </p>
                  </div>
                  <div className="text-xs text-slate-400 flex-shrink-0 font-mono">{formatDate(ev.created_at)}</div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
          <span className="text-slate-500">
            {events.items.length > 0 ? `${skip + 1} – ${skip + events.items.length}` : '0'} von {events.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSkip(Math.max(0, skip - limit))}
              disabled={skip === 0}
              className="border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40 rounded"
              data-testid="audit-prev-page"
            >Zurück</button>
            <button
              type="button"
              onClick={() => setSkip(skip + limit)}
              disabled={skip + events.items.length >= events.total}
              className="border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40 rounded"
              data-testid="audit-next-page"
            >Weiter</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusTile({ icon: Icon, label, value, ok, hint, testId }) {
  const tone = ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800';
  return (
    <div className={`border p-5 rounded ${tone}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon weight="bold" className="w-5 h-5" />
          <p className="text-xs uppercase tracking-widest font-bold">{label}</p>
        </div>
        {ok ? <CheckCircle weight="fill" className="w-5 h-5 text-emerald-600" /> : <Warning weight="fill" className="w-5 h-5 text-amber-500" />}
      </div>
      <p className="font-heading text-lg font-bold">{value || '—'}</p>
      {hint && <p className="text-xs opacity-75 mt-1 truncate">{hint}</p>}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const colors = {
    red: 'border-red-200 bg-red-50 text-red-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    violet: 'border-violet-200 bg-violet-50 text-violet-800',
  };
  return (
    <div className={`border p-3 rounded ${colors[tone] || colors.amber}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold opacity-75">{label}</p>
      <p className="font-mono text-2xl font-black mt-1">{value ?? 0}</p>
    </div>
  );
}
